// pages/.../PrivilegeAccess.jsx
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import PortalDataTable from "../../components/PortalDataTable";
import { SearchBox } from "../../components";
import { myDeskColumns } from "../../utils";
import { useGetPrivilegeAccessForms, useGetSearchedPrivilege } from '../../hooks/forms/useGet';
import NetworkError from '../../components/NetworkError';
import { Modal } from '../../components';
import SearchForm from '../../forms/create/searchForm';

const PrivilegeAccess = () => {
  const { officerId, roleId } = JSON.parse(localStorage.getItem('user') || "{}");

  // pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // base (privilege-scoped) list
  const {
    data: basePage,
    isLoading: isBaseLoading,
    isError: isBaseError,
    error: baseError
  } = useGetPrivilegeAccessForms(officerId, "", "", page, pageSize);

  // modal filters (BranchId, FormTypeId, StartDate, EndDate, etc.)
  const [filters, setFilters] = useState(null);

  // search box -> debounced query (min 3 chars)
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const q = query.trim();
      setDebouncedQuery(q.length >= 3 ? q : null);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  // reset page when filter context changes
  useEffect(() => {
    setPage(1);
  }, [officerId, JSON.stringify(filters), debouncedQuery]);

  // effective search payload (only triggers when meaningful)
  const effectiveFilters = useMemo(() => {
    if (!officerId) return null;
    const base = {
      PageNumber: page,
      PageSize: pageSize,
      rIdx: roleId,
    };
    if (filters) Object.assign(base, filters);
    if (debouncedQuery) base.RequestValue = debouncedQuery;

    const hasMeaningful =
      !!base.RequestValue ||
      !!base.TicketNo ||
      (base.FormTypeId && Number(base.FormTypeId) > 0) ||
      !!base.StartDate ||
      !!base.EndDate ||
      (base.BranchId && Number(base.BranchId) > 0);

    return hasMeaningful ? base : null;
  }, [officerId, roleId, filters, debouncedQuery, page, pageSize]);

  // search hook
  const {
    data: searchPage,
    isLoading: isSearching,
    isError: isSearchError,
    error: searchError
  } = useGetSearchedPrivilege(officerId, effectiveFilters);

  const searchActive = !!effectiveFilters;

  const [toggleModal, setToggleModal] = useState(false);
  const handleModalToggle = () => setToggleModal(s => !s);

  // choose active page (base vs search)
  const activePage = searchActive ? (searchPage ?? {}) : (basePage ?? {});
  const rows = activePage.items ?? [];
  const currentPage = activePage.pageNumber ?? page;
  const totalPages =
    typeof activePage.totalPages === "number"
      ? activePage.totalPages
      : (activePage.hasNext ? currentPage + 1 : currentPage);
  const hasPrevious = activePage.hasPrevious ?? currentPage > 1;
  const hasNext = activePage.hasNext ?? (rows.length >= pageSize);

  const loading = searchActive ? isSearching : isBaseLoading;

  // errors
  if (isBaseError && baseError?.code === 'ERR_NETWORK') return <NetworkError error={baseError} />;
  if (isBaseError) return <span>Oops something happened....</span>;
  if (isSearchError) console.warn("Privilege search error:")//, searchError);

  // paging handlers
  const clearAll = () => { setFilters(null); setQuery(""); setPage(1); };
  const next = () => hasNext && setPage(p => p + 1);
  const prev = () => hasPrevious && setPage(p => Math.max(1, p - 1));
  const gotoPage = (p) => setPage(Math.max(1, p));
  const changePageSize = (sz) => { setPageSize(sz); setPage(1); };

  // -------- Export helpers: flatten forms -> rows for Excel -----------------
  const flattenForExport = (forms = []) => {
    // collect all unique labels across the selection
    const labelSet = new Set();
    forms.forEach(f =>
      (f.formValues || []).forEach(v => {
        const lbl = v?.formField?.label ?? `Field ${v?.formFieldId ?? ""}`.trim();
        if (lbl) labelSet.add(lbl);
      })
    );
    const labels = Array.from(labelSet);

    // map each form into a simple object row
    const data = forms.map(f => {
      const row = {
        Ticket: f.ticketNumber ?? "",
        FormType: f.formType?.formTypeName ?? "",
        DateFilled: f.dateFilled ? new Date(f.dateFilled).toLocaleString() : "",
      };
      labels.forEach(lbl => {
        const match = (f.formValues || []).find(v => (v?.formField?.label ?? "") === lbl);
        row[lbl] = match?.formData ?? "";
      });
      return row;
    });

    return { labels, data };
  };

  const exportToExcel = () => {
    const forms = rows || [];
    if (!forms.length) return;

    const { data } = flattenForExport(forms);
    const ws = XLSX.utils.json_to_sheet(data);

    // optional: set column widths a bit nicer
    const header = Object.keys(data[0] || {});
    ws['!cols'] = header.map(h => ({ wch: Math.max(12, String(h).length + 2) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PrivilegeForms");
    const fileName = `privilege_forms_export_${new Date().toISOString().slice(0,10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="flex flex-col">
      <div className="branch_header">
        <div className="branch_header_title_group">
          <div className="uppercase text-lg font-bold">My Privilege</div>
          {!loading && (
            <div className="uppercase text-xs py-1.5 text-start text-gray-600">
              {rows?.length === 0 ? 'No ' : rows.length} requests
            </div>
          )}
        </div>

        {/* Right side controls (Clear + Export) */}
        <div className="flex items-center gap-3">
          <button
            className="text-[#0E86D4] cursor-pointer"
            onClick={clearAll}
            title="Clear Filters"
          >
            Clear Filters
          </button>

          {/* Export button */}
          <button
            type="button"
            onClick={exportToExcel}
            disabled={!rows?.length}
            title="Export current results to Excel"
            className={`px-3 py-1.5 rounded border text-sm ${
              rows?.length ? 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            }`}
          >
            Export Excel
          </button>
        </div>
      </div>

      <SearchBox
        placeholder="Search requests"
        value={query}
        onChange={setQuery}
        _function={handleModalToggle}
      />

      {/* Optional: page size selector */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm text-gray-600">Page size:</span>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={pageSize}
          onChange={(e) => changePageSize(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map(sz => <option key={sz} value={sz}>{sz}</option>)}
        </select>
      </div>

      <PortalDataTable
        cols={myDeskColumns}
        loading={loading}
        rows={rows}
        currentPage={currentPage}
        totalPages={totalPages}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        next={next}
        previous={prev}
        onPageChange={gotoPage}
      />

      <Modal
        title="Search Filter"
        form={
          <SearchForm
            onApply={(payload) => {
              const { PageNumber, PageSize, ...rest } = payload || {};
              if (PageSize) setPageSize(Number(PageSize));
              setPage(PageNumber ? Number(PageNumber) : 1);
              setFilters(rest);
            }}
            onClose={handleModalToggle}
          />
        }
        toggleModal={toggleModal}
        handleToggle={handleModalToggle}
      />
    </div>
  );
};

export default PrivilegeAccess;
